import { Injectable } from '@angular/core';
import { ContentStatusEnum, OrientationEnum, ContentEnum, HeadFootEnum, ContentElement, ImageContentElement, GridCellClass, GridCellHeader, GridCellFooter, NormalizeEnumsService } from '@urbannex/storethemes';
import { EditorInputField } from '../../../editor-container/editor-model';
import { CellEditorStoreService } from '../../cell-editor-store.service';
import { sectionEditorIcons } from '../section-editor-dialog/section-dialog-menu-items';
import { NbIconLibraries, NbMenuItem } from '@nebular/theme';
import { BehaviorSubject } from 'rxjs';

export interface Cell {
  cellId?:string;
  status?:ContentStatusEnum;
  imageOrientation?:OrientationEnum;
  imgSrc?:string,
  bgImgSrc?:string;
  width?:number,
  height?:number,
  layoutClass?:string;
  fillClass?:string;
  fillColor?:string;
  bgColor?:string;
  heightCls?:string;
  themeClass?:string;
  contentEnum?:ContentEnum;
  headerFooterEnum?:HeadFootEnum;
  bannerText?:string;
  hdrParBgColor?:string;
  hdrContLayoutClass?:string;
  contentElements?:ContentElement[];
  images?:ImageContentElement[];
  contentWidth?:string;
  contentHeight?:string;
  color?:string,
  cellVerticalAlign?:string;
  cellHorizontalAlign?:string;
  borderWidth?:string;
  borderColor?:string;
  borderStyle?:string;
  borderRadius?:string;
  border?:string;
}
export interface GridCell extends Cell{
  colspan?:number;
  rowspan?:number;
  className?:GridCellClass;
  linkId?:string;
  gridRowStart?:number,
  gridRowEnd?:number,
  gridColumnStart?:number,
  gridColumnEnd?:number,
  heading?:GridCellHeader,
  footer?:GridCellFooter,
  bannerText?:string,
  contentEnum?:ContentEnum;
}
export interface RowCell extends Cell{
  className?:GridCellClass;
  linkId?:string;
  altImgSrc?:string;
  heading?:GridCellHeader
  footer?:GridCellFooter
}

@Injectable({
  providedIn: 'root'
})
export class CellOptionsService {
  private editorInputFields: EditorInputField[] = [];
  private currentCellData: GridCell | RowCell = null;
  contentEnum:ContentEnum;

  // Subject for menu items
  private menuItemsSubject = new BehaviorSubject<NbMenuItem[]>([]);
  public menuItems$ = this.menuItemsSubject.asObservable();

  defaultIcon = {icon:"text-edit",pack:"section-menu-icons"}

  baseFields: EditorInputField[] = [
    {
      name: 'bgColor',
      title: 'Background Color',
      type: '',
      controlType: 'color-picker',
      popoverData:{'title':'Background Color',popoverType:'background',target:'cell',service:'cellEditorStoreService',property:'background'},
      value: "",
      fieldIcon:{icon:"background-color",pack:"section-menu-icons"},
      context: {
        popOver: true,
        target: "cell",
        property: "background"
      }
    },
    {
      name: 'layoutClass',
      title: 'Layout Class',
      type: 'string',
      controlType: 'select',
      value: '',
      fieldIcon:{icon:"layout",pack:"section-menu-icons"},
      popoverData:{'title':'Layout Class',popoverType:'layout',target:'cell',service:'cellEditorStoreService',property:'layoutClass'},
      selectOptions: [
        { description: "3 Cells", key: "grid-3" },
        { description: "4 Cells", key: "grid-4" },
        { description: "5 Cells", key: "grid-5" },
        { description: "6 Cells", key: "grid-6" },
        { description: "7 Cells", key: "grid-7" },
        { description: "8 Cells", key: "grid-8" },
        { description: "9 Cells", key: "grid-9" }
      ]
    },
    {
      name: 'border',
      title: 'Border',
      type: 'string',
      controlType: 'select',
      value: 'vertical',
      fieldIcon:{icon:"border",pack:"section-menu-icons"},
      popoverData:{title:'Cell Border',popoverType:'border',target:'cell',service:'cellEditorStoreService'},
      selectOptions: [
        { description: "Vertical", key: "vertical" },
        { description: "Horizontal", key: "horizontal" }
      ]
    },
    {
      name: 'bgImgSrc',
      title: 'Background Image',
      type: 'dialog',
      controlType: '',
      fieldIcon:{icon:"image",pack:"section-menu-icons"},
      popoverData:{title:'Set Background Image',popoverType:'imageDialog',target:'cell',service:'cellEditorStoreService',property:'background-image'},
    },
    {
      name: 'color',
      title: 'Text Color',
      type: '',
      controlType: 'color-picker',
      popoverData:{'title':'Text Color',popoverType:'color',target:'section',service:'sectionStore',property:'text'},
      value:"",
      fieldIcon:{icon:"text-color",pack:"section-menu-icons"},
      context: {
        popOver: true,
        target: "cell",
        property: "text"
      }
    },
    {
      name:'overLay',
      title:'Overlay',
      type:'',
      controlType:'',
      popoverData:{
        title: 'Overlay',
        popoverType: 'layer',
        target: '',
        service:'cellEditorStoreService'
      },
      value:'',
      fieldIcon:{icon:"layers",pack:"section-menu-icons"},
      context:{
        isPopup:true
      }
    }
  ]
  
  constructor(
    private cellEditorStoreService:CellEditorStoreService,
    private normalizeEnumsService:NormalizeEnumsService,
    private iconLibraries: NbIconLibraries
  ) { }

  initializeSelectionOptions(selectedCell: GridCell | RowCell): void {
    this.iconLibraries.registerSvgPack('section-menu-icons',sectionEditorIcons);
    const { contentElements, ...newObject } = selectedCell;
    this.currentCellData = newObject;
    console.log('Current Cell Data:', this.currentCellData);
    this.setContentEnumAndOptions(selectedCell);
    this.setEditorInputFields();
    this.populateFieldValues();
    /**
     * Generate menu items for sidebar
     */
    this.generateMenuItems();
   }

  setContentEnumAndOptions(selectedCell: GridCell | RowCell): void {
    this.contentEnum = selectedCell.contentEnum;
    const contentEnumString = this.normalizeEnumsService.normalise(ContentEnum.PRODUCT);
    console.log('Content Enum String:', contentEnumString);
    
    //Set the editor input fields based on the content enum
    switch (contentEnumString) {
      case "product content":
      case "category content":
      case "promotional text:":
      case "call to action:":
      case "blog article Link:":
      case "category Link:":
      case "product Link:":
      case "banner:":
      case "headline:":
      case "sub headline:":
      case "gallery:":
      case "product classification:":
      case "product identification:":
      case "product standard features:":
      case "product selectable features:":
      case "product price:":
      case "product rating:":
      case "product rating:":
      case "product packaging:":
      case "product short description:":
      case "product long description:":
      case "product reviews:":
      case "add to cart:":
      case "product share:":
      case "delivery:":
      case "product variant:":
      case "general text:":
      case "shopping cart:":
      case "blog header:":
      case "blog summary:":
      case "blog main:":
      case "blog share:":
      case "blog comments:":
      case "blog image:":
      case "navigation list:":
      case "product cell:":
      case "hero image:":
      case "feature:":
      case "testimonial":
      case "inventory:":
      case "ContentEnum.PRODUCT_PACKAGING": 
      case "ContentEnum.PRODUCT_SHORT_DESC": 
      case "ContentEnum.PRODUCT_LONG_DESC": 
      case "ContentEnum.PRODUCT_REVIEWS": 
      case "ContentEnum.PRODUCT_ADD_TO_CART": 
      case "ContentEnum.PRODUCT_SHARE": 
      case "ContentEnum.PRODUCT_DELIVERY": 
      case "ContentEnum.PRODUCT_INVENTORY": 
      case "ContentEnum.PRODUCT_VARIANT":
      case "ContentEnum.GENERAL_TEXT":
      case "ContentEnum.SHOPPINGCART": 
      case "ContentEnum.BLOG_HEADER": 
      case "ContentEnum.BLOG_SUMMARY":
      case "ContentEnum.BLOG_MAIN":
      case "ContentEnum.BLOG_SHARE":
      case "ContentEnum.TESTIMONIAL": 
      case "ContentEnum.BLOG_COMMENTS": 
      case "ContentEnum.BLOG_IMAGE": 
      case "ContentEnum.NAV_LIST":
      case "ContentEnum.PRODUCT_CELL":
      case "ContentEnum.HERO_IMAGE":
      case "ContentEnum.FEATURE": 
        console.log(contentEnumString)
        break;
      default:
        console.log(contentEnumString)
        break;   
            
              
    }
  }

  setEditorInputFields():void{
    this.editorInputFields = this.getFieldDefinitions(this.contentEnum);
  }
  
  getEditorInputFields(): EditorInputField[] {
    return this.editorInputFields;
  }

  getFieldDefinitions(contentEnum:ContentEnum | string): EditorInputField[] {
    let fields: EditorInputField[] = [...this.baseFields];
    // Add more fields based on contentEnum if needed
    let contentEnumString = this.normalizeEnumsService.normalise(contentEnum);
    switch (contentEnumString) {
      case "product content":
      case "category content":
      case "promotional text:":
      case "call to action:":
      case "blog article Link:":
      case "category Link:":
      case "product Link:":
      case "banner:":
      case "headline:":
      case "sub headline:":
      case "gallery:":
      case "product classification:":
      case "product identification:":
      case "product standard features:":
      case "product selectable features:":
      case "product price:":
      case "product rating:":
      case "product rating:":
      case "product packaging:":
      case "product short description:":
      case "product long description:":
      case "product reviews:":
      case "add to cart:":
      case "product share:":
      case "delivery:":
      case "product variant:":
      case "general text:":
      case "shopping cart:":
      case "blog header:":
      case "blog summary:":
      case "blog main:":
      case "blog share:":
      case "blog comments:":
      case "blog image:":
      case "navigation list:":
      case "product cell:":
      case "hero image:":
      case "feature:":
      case "testimonial":
      case "inventory:":
        // Add specific fields for these content types
        /* fields.push(
          {
            name: 'additionalField1',
            title: 'Additional Field 1',
            type: 'string',
            controlType: 'text',
            value: ''
          },
          {
            name: 'additionalField2',
            title: 'Additional Field 2',
            type: 'number',
            controlType: 'number',
            value: 0
          }
        ); */
        break;
    }
    return fields;
  }

  populateFieldValues(){
    console.log(this.editorInputFields);
  }

  generateMenuItems(){
    const menuItems: NbMenuItem[] = this.editorInputFields.map(field => ({
        title: field.title,
        icon: field.fieldIcon ? field.fieldIcon : this.defaultIcon,
        data: field
      }));
        console.log('Generated menu items:', menuItems);
    
        this.menuItemsSubject.next(menuItems);

  }
}
